import React, { useState } from 'react';
import {
  Box,
  Flex,
  Button,
  HStack,
  VStack,
  Link,
  Container,
  Image,
  IconButton,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { FiMenu, FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const LandingNavbar: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const borderColor = 'gray.200';

  // Check if we're on a dashboard page
  const isOnDashboard = location.pathname.startsWith('/dashboard') || 
                       location.pathname.startsWith('/reviews') || 
                       location.pathname.startsWith('/create-review') || 
                       location.pathname.startsWith('/settings');

  const navLinks = (
    <>
      <Link href="/#features" _hover={{ color: 'brand.500' }} cursor="pointer" onClick={() => setMobileMenuOpen(false)}>
        {t('landing.navFeatures')}
      </Link>
      <Link href="/#pricing" _hover={{ color: 'brand.500' }} cursor="pointer" onClick={() => setMobileMenuOpen(false)}>
        {t('landing.navPricing')}
      </Link>
      <Link href="/#faq" _hover={{ color: 'brand.500' }} cursor="pointer" onClick={() => setMobileMenuOpen(false)}>
        {t('landing.navFaq')}
      </Link>
      <Link href="/#contact" _hover={{ color: 'brand.500' }} cursor="pointer" onClick={() => setMobileMenuOpen(false)}>
        {t('landing.navContact')}
      </Link>
    </>
  );

  return (
    <Box
      borderBottom="1px"
      borderColor={borderColor}
      position="sticky"
      top={0}
      zIndex={10}
      backdropFilter="blur(10px)"
      bg="rgba(255, 255, 255, 0.9)"
    >
      <Container maxW="6xl">
        <Flex h={20} alignItems="center" justifyContent="space-between">
          {/* Logo */}
          <Image
            src="/neurev-logo.png"
            alt={t('landing.logoAlt')}
            h={16}
            cursor="pointer"
            onClick={() => window.location.href = '/'}
          />

          {/* Desktop Navigation Links */}
          <HStack gap={8} display={{ base: 'none', md: 'flex' }}>
            {navLinks}
          </HStack>

          {/* Auth Buttons + Mobile Menu Toggle */}
          <HStack gap={4}>
            <LanguageSwitcher />
            {isAuthenticated ? (
              <>
                {isOnDashboard ? (
                  <Button
                    {...{as: RouterLink, to: "/"} as any}
                    variant="outline"
                    colorScheme="brand"
                    size="sm"
                  >
                    {t('landing.goToWebsite')}
                  </Button>
                ) : (
                  <Button
                    {...{as: RouterLink, to: "/dashboard"} as any}
                    colorScheme="brand"
                    size="sm"
                  >
                    {t('landing.goToDashboard')}
                  </Button>
                )}
                <Button
                  onClick={logout}
                  variant="outline"
                  colorScheme="red"
                  size="sm"
                  display={{ base: 'none', md: 'flex' }}
                >
                  {t('landing.signOut')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  {...{as: RouterLink, to: "/login"} as any}
                  variant="outline"
                  colorScheme="brand"
                  size="sm"
                  display={{ base: 'none', md: 'flex' }}
                >
                  {t('landing.signIn')}
                </Button>
                <Button
                  {...{as: RouterLink, to: "/register"} as any}
                  colorScheme="brand"
                  size="sm"
                  display={{ base: 'none', md: 'flex' }}
                >
                  {t('landing.getStarted')}
                </Button>
              </>
            )}

            {/* Mobile Hamburger */}
            <IconButton
              aria-label={t('landing.toggleMenu')}
              variant="ghost"
              display={{ base: 'flex', md: 'none' }}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              size="sm"
            >
              {mobileMenuOpen ? <FiX size={20} /> : <FiMenu size={20} />}
            </IconButton>
          </HStack>
        </Flex>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <Box display={{ base: 'block', md: 'none' }} pb={4}>
            <VStack gap={4} align="stretch">
              {navLinks}
              {isAuthenticated ? (
                <Button
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  variant="outline"
                  colorScheme="red"
                  size="sm"
                  w="full"
                >
                  {t('landing.signOut')}
                </Button>
              ) : (
                <>
                  <Button
                    {...{as: RouterLink, to: "/login"} as any}
                    variant="outline"
                    colorScheme="brand"
                    size="sm"
                    w="full"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('landing.signIn')}
                  </Button>
                  <Button
                    {...{as: RouterLink, to: "/register"} as any}
                    colorScheme="brand"
                    size="sm"
                    w="full"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('landing.getStarted')}
                  </Button>
                </>
              )}
            </VStack>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default LandingNavbar;
